import { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { FileUploader } from 'react-drag-drop-files';
import Stack from '@mui/material/Stack';
import Rating from '@mui/material/Rating';
import { styled } from '@mui/material/styles';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LensIcon from '@mui/icons-material/Lens';
import FieldView from '@CommonViews/FieldView';
import DeleteFieldForm from '@CommonForms/DeleteFieldForm';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import BoxOptions from '@CommonFunctionalDisplay/BoxOptions';
import KaiziqResolveConnectionService from '@Helpers/URLHelpers/KaiziqResolveConnectionService';
import FieldService from '@Helpers/URLHelpers/FieldService';
import ErrorHandler from '@Root/ErrorHandler';
import FieldPageService from '@Helpers/URLHelpers/FieldPageService';
import { EditorState, convertToRaw, convertFromRaw, ContentState } from 'draft-js';
import PhoneInput from 'react-phone-input-2'
import { Emoji } from 'emoji-picker-react';
import 'react-phone-input-2/lib/material.css'
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';
import './SettingsFieldRow.scss';

export default function SettingsFieldRow(props) {

    const [cursor, setCursor] = useState('auto');
    const [defaultOption, setDefaultOption] = useState({});
    const [showDeleteFieldForm, setShowDeleteFieldForm] = useState(false);
    const [showFieldView, setShowFieldView] = useState(false);
    const [informationFieldEditorState, setInformationFieldEditorState] = useState(EditorState.createEmpty());
    const [fieldTextSize, setFieldTextSize] = useState(0);
    const [multiSelectChecked, setMultiSelectChecked] = useState([]);
    const [rules, setRules] = useState([]);
    const [ratingRange, setRatingRange] = useState(5);
    const [ratingType, setRatingType] = useState('');
    const [ratingScaleIcon, setRatingScaleIcon] = useState('star');
    const [ratingInitialLabel, setRatingInitialLabel] = useState('');
    const [ratingFinalLabel, setRatingFinalLabel] = useState('');
    const [options, setOptions] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [pageBackground, setPageBackground] = useState({});
    const errorHandler = new ErrorHandler();
    const kaiziqResolveConnectionService = new KaiziqResolveConnectionService();
    const fieldPageService = new FieldPageService();
    const fieldService = new FieldService();
    const className = 'settingsFieldRow';

    const HeartStyledRating = styled(Rating)({
      '& .MuiRating-iconFilled': {
        color: '#ff6d75',
      },
      '& .MuiRating-iconHover': {
        color: '#ff3d47',
      },
    });

    const CircleStyledRating = styled(Rating)({
      '& .MuiRating-iconFilled': {
        color: '#2784B9d6',
      },
      '& .MuiRating-iconHover': {
        color: '#2784B9',
      },
    });

    useEffect(() => {
        let rulesObj = [];
        let optionsObj = [];
        if(!!props.field?.rules) {
            rulesObj = JSON.parse(props.field?.rules);
            setRules(rulesObj);
        }
        if(!!props.field?.options) {
            optionsObj = JSON.parse(props.field?.options);
            setOptions(optionsObj);
        }
        if(props.field?.type == 'information') {
            let ruleIndex = rulesObj?.findIndex((rule) => rule.type === 'text');
            if(!!rulesObj[ruleIndex].value) {
                setInformationFieldEditorState(EditorState.createWithContent(convertFromRaw(JSON.parse(rulesObj[ruleIndex].value))));
            } else {
                const contentBlock = htmlToDraft('');
                if (contentBlock) {
                    const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                    const editorStateObj = EditorState.createWithContent(contentState);
                    setInformationFieldEditorState(editorStateObj);
                }
            }
        }
        if(props.field?.type == 'multiChoice') {
            setDefaultOption(optionsObj?.find((option) => option.id === rulesObj.find((rule) => rule.type === 'default')?.value))
        }
        if(props.field?.type == 'rating') {
            setRatingRange(rulesObj.find((rule) => rule.type === 'width')?.value)
            setRatingType(rulesObj.find((rule) => rule.type === 'ratingType')?.value)
            setRatingScaleIcon(rulesObj.find((rule) => rule.type === 'scaleIcon')?.value)
            setRatingFinalLabel(rulesObj.find((rule) => rule.type === 'ratingFinalLabel')?.value)
            setRatingInitialLabel(rulesObj.find((rule) => rule.type === 'ratingInitialLabel')?.value)
        }
        setPageBackground(props?.formDetails?.settings?.theme?.formFieldTheme?.formFieldThemeBackground);
    }, [props])

    const toggleDeleteFieldForm = () => {
        setShowDeleteFieldForm(!showDeleteFieldForm);
    }

    const toggleRequiredField = () => {
        let fieldObj = props.field;
        let rules = JSON.parse(fieldObj.rules);
        let ruleIndex = rules?.findIndex((rule) => rule.type === 'required');
        if(ruleIndex === -1) {
            let rule = {
                id: rules?.length + 1,
                type: 'required',
                value: true
            }
            rules?.push(rule);
        } else {
            rules[ruleIndex]['value'] = !rules[ruleIndex]['value'];
        }
        fieldObj.rules = JSON.stringify(rules);
        updateField(fieldObj)
    }

    const toggleShowFieldView = () => {
        setShowFieldView(!showFieldView);
    }

    const handleTextFieldChange = (e) => {
        let sanitizedText = e.target.value;
        setFieldTextSize(sanitizedText.length);
    }

    const handleFieldViewClick = () => {
        toggleShowFieldView();
    }

    const updateField = async(fieldObj) => {
        setCursor('wait');
        try {
            let parentFieldId = fieldObj.id;
            let fieldsListObj = JSON.parse(props.kaiziqResolveConnection.details).fields['form'];
            let parentFieldIndex = fieldsListObj.findIndex((fieldId) => fieldId === parentFieldId);
            fieldObj = await fieldService.updateField(fieldObj);
            fieldObj = JSON.parse(fieldObj.body);

            fieldsListObj[parentFieldIndex] = fieldObj.id;
            let detailsFieldHistoryObj = JSON.parse(props.kaiziqResolveConnection.details).fields['fieldHistory'];
            detailsFieldHistoryObj[fieldObj.parentFieldId]['current'] = fieldObj.id;
            detailsFieldHistoryObj[fieldObj.parentFieldId]['fieldHistory'].push(fieldObj.id);
            let detailsObj = JSON.parse(props.kaiziqResolveConnection.details);
            detailsObj.fields['form'] = fieldsListObj;
            detailsObj.fields['fieldHistory'] = detailsFieldHistoryObj;
            await kaiziqResolveConnectionService.updateKaiziqResolveConnectionDetails(props.kaiziqResolveConnection?.id, JSON.stringify(detailsObj));


            let pageFieldsListObj = JSON.parse(props.fieldPage.fields);
            parentFieldIndex = pageFieldsListObj.findIndex((fieldId) => fieldId === parentFieldId);
            pageFieldsListObj[parentFieldIndex] = fieldObj.id;
            let fieldPageObj = props.fieldPage;
            fieldPageObj.fields = JSON.stringify(pageFieldsListObj);
            await fieldPageService.updateFieldPage(fieldPageObj);
            setShowFieldView(false);
            await props.renderData();
        } catch(err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    }

    const handleMultiSelectOptionClick = (id) => {
        let multiSelectCheckedList = multiSelectChecked;
        if(multiSelectChecked.some((multiSelectId) => multiSelectId === id)) {
            multiSelectCheckedList = multiSelectCheckedList.filter((multiSelectId) => multiSelectId !== id);
        } else {
            multiSelectCheckedList.push(id);
        }
        setMultiSelectChecked(multiSelectCheckedList);
        setRefresh(!refresh);
    }

    const handleDateChange = (date) => {

    }

    return (
        <div className={className} style={{background: pageBackground?.type === 'solid' ? pageBackground?.color : 'transparent'}}>
            <div className={className + '__inner width-wfa'}>
                <div className={className + '__inner__top width-wfa flex-row-center-space-between'}>
                    <div className={className + '__inner__top__left'}>
                        <div className={className + '__inner__required'}>
                            {
                                props.field?.type !== 'information' &&
                                <FormGroup className={className + '__inner__required__check'}>
                                    <FormControlLabel control={
                                        <Checkbox
                                            disabled={props.kaiziqResolveConnection.connectionLive || cursor === 'wait'}
                                            checked={!!props.field?.rules ?
                                                    JSON.parse(props.field?.rules)?.find((rule) => rule.type === 'required')?.value
                                                :
                                                    false}
                                            onChange={toggleRequiredField}/>
                                        }
                                        label='Required' />
                                </FormGroup>
                            }
                        </div>
                    </div>
                    <div className={className + '__inner__actions '}>
                        <BoxOptions
                            hovered
                            showShiftUp
                            showShiftDown
                            showSettings
                            showView={false}
                            shiftUpDisabled={props.index === 0 || props.kaiziqResolveConnection.connectionLive}
                            settingsDisabled={props.kaiziqResolveConnection.connectionLive}
                            deleteDisabled={props.kaiziqResolveConnection.connectionLive}
                            shiftDownDisabled={props.index === (props.fieldPageFieldIdList.length - 1) || props.kaiziqResolveConnection.connectionLive}
                            handleSettingsClickCallback={handleFieldViewClick}
                            handleShiftUpClickCallback={() => props.shiftFieldUp(props.index)}
                            handleShiftDownClickCallback={() => props.shiftFieldDown(props.index)}
                            handleDeleteClickCallback={toggleDeleteFieldForm}/>
                    </div>
                </div>
                <div className={className + '__inner__field width-wfa flex-column-flex-start-2vh-gap '}>
                    <div className={className + '__inner__field__title semi-bold-text standard-text-semi-large width-wfa'}>
                        {'F' + (props?.index + 1) + ': ' + props?.field?.title}
                        {
                            (!!props.field?.rules && props.field?.type !== 'information') ?
                                JSON.parse(props.field?.rules)?.find((rule) => rule.type === 'required')?.value ?
                                    '*'
                                :
                                    ''
                            :
                                ''
                        }
                    </div>
                    {
                        props.field?.type == 'text' &&
                            <div className={className + '__inner__field__input width-wfa flex-column-flex-start-1vh-gap'}>
                                <TextField
                                    placeholder={JSON.parse(props.field?.rules)?.find((rule) => rule.type === 'default')?.value}
                                    className={className + '__inner__field__input__textField width-wfa'}
                                    type='text'
                                    onChange={handleTextFieldChange}
                                    inputProps={{ maxLength: JSON.parse(props.field?.rules)?.find((rule) => rule.type === 'width')?.value ?? 250 }}
                                    maxRows={(JSON.parse(props.field?.rules)?.find((rule) => rule.type === 'width')?.value/250) + 1}
                                    minRows={JSON.parse(props.field?.rules)?.find((rule) => rule.type === 'width')?.value/250}
                                    multiline
                                    variant='outlined'
                                    required/>
                                <div className={className + '__inner__field__input__size flex-column-flex-end-2vh-gap semi-bold-text width-wfa '
                                    + ((fieldTextSize >= JSON.parse(props.field?.rules).find((rule) => rule.type === 'width')?.value) ? ' red-text' : '')}>
                                    {fieldTextSize}/{JSON.parse(props.field?.rules).find((rule) => rule.type === 'width')?.value}
                                </div>
                            </div>
                    }
                    {
                        props.field?.type == 'number' &&
                            <div className={className + '__inner__field__input width-wfa flex-column-flex-start-1vh-gap'}>
                                <div className={className + '__inner__field__input__value  flex-column-flex-start-1vh-gap'}
                                    style={{width: ((JSON.parse(props.field?.rules)?.find((rule) => rule.type === 'digitWidth')?.value
                                        ? JSON.parse(props.field?.rules)?.find((rule) => rule.type === 'digitWidth')?.value + 5
                                        : '15') + 'rem')}}>
                                    <TextField
                                        className={className + '__inner__body__field__number__value__textField'}
                                        type='number'
                                        size='small'
                                        InputProps={{
                                            inputProps: {
                                                min: (JSON.parse(props.field?.rules)?.find((rule) => rule.type === 'rangeMin')?.value),
                                                max: (JSON.parse(props.field?.rules)?.find((rule) => rule.type === 'rangeMax')?.value)
                                            }
                                        }}
                                        placeholder={JSON.parse(props.field?.rules)?.find((rule) => rule.type === 'default')?.value}
                                        maxRows='1'
                                        variant='outlined'/>
                                </div>
                            </div>
                    }
                    {
                        props.field?.type == 'date' &&
                            <div className={className + '__inner__field__input width-wfa flex-column-flex-start-1vh-gap'}>
                                <div className={className + '__inner__field__input__value flex-column-center'}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <Stack spacing={3}>
                                            {
                                                rules?.find((rule) => rule.type === 'timeSelect')?.value ?
                                                    <MobileDateTimePicker
                                                      className={className + '__inner__field__input__value__input'}
                                                      onChange={handleDateChange}
                                                      renderInput={(params) => <TextField variant="outlined" {...params} />}
                                                    />
                                                :
                                                    <DesktopDatePicker
                                                        inputFormat="DD/MM/YYYY"
                                                        onChange={handleDateChange}
                                                        renderInput={(params) => <TextField variant="outlined" {...params} />}
                                                    />
                                            }
                                        </Stack>
                                    </LocalizationProvider>
                                </div>
                            </div>
                    }
                    {
                        props.field?.type == 'information' &&
                            <div className={className + '__inner__field__value width-wfa'}
                                dangerouslySetInnerHTML={{__html: draftToHtml(convertToRaw(informationFieldEditorState.getCurrentContent()))}}/>
                    }
                    {
                        props.field?.type == 'file' &&
                            <div className={className + '__inner__field__value width-wfa'}>
                                <div className={className + '__inner__field__value__fileDrop width-wfa'}>
                                    <FileUploader label='Upload or drop a file right here. Max Size 2MB !'/>
                                </div>
                            </div>
                    }
                    {
                        ( props.field?.type === 'multiChoice' || props.field?.type === 'condition' ) &&
                            <div className={className + '__inner__field__input width-wfa flex-column-flex-start-1vh-gap'}>
                                <FormControl>
                                    <RadioGroup
                                        defaultValue={defaultOption?.id}>
                                        {
                                            options?.map((option) =>
                                                <FormControlLabel value={option.id} control={<Radio />} label={
                                                    <div className={className + '__inner__body__field__multiChoice__value__select__option flex-row-center-1vw-gap'}>
                                                        <div className={className + '__inner__body__field__multiChoice__value__select__option indicator-box'}
                                                            style={{background: option.color}}/>
                                                        {option.title}
                                                    </div>
                                                } />
                                            )
                                        }
                                    </RadioGroup>
                                </FormControl>
                            </div>
                    }
                    {
                        props.field?.type === 'multiSelect' &&
                            <div className={className + '__inner__field__input width-wfa flex-column-flex-start-1vh-gap'}>
                                <FormControl>
                                    <FormGroup>
                                        {
                                            options?.map((option) =>
                                                <FormControlLabel
                                                    control={
                                                      <Checkbox checked={multiSelectChecked.some((multiSelectId) => multiSelectId === option.id)} onChange={() => handleMultiSelectOptionClick(option.id)} name={option.id} />
                                                    }
                                                    label={
                                                        <div className={className + '__inner__body__field__multiChoice__value__select__option flex-row-center-1vw-gap'}>
                                                            <div className={className + '__inner__body__field__multiChoice__value__select__option indicator-box'}
                                                                style={{background: option.color}}/>
                                                            {option.title}
                                                        </div>
                                                    }/>
                                            )
                                        }
                                    </FormGroup>
                                </FormControl>
                            </div>
                    }
                    {
                        props.field?.type == 'duration' &&
                            <div className={className + '__inner__field__input width-wfa flex-column-flex-start-1vh-gap'}>
                                <div className={className + '__inner__field__input__duration flex-row-center-1vw-gap'}>
                                    <TextField
                                        className={className + '__inner__field__input__duration__textField'}
                                        type='number'
                                        size='small'
                                        variant='outlined'
                                        InputProps={{ inputProps: { min: 0} }}
                                        required/>
                                    <TextField
                                        className={className + '__inner__field__input__duration__textField'}
                                        type='number'
                                        size='small'
                                        InputProps={{ inputProps: { min: 0, max: 24 } }}
                                        variant='outlined'
                                        required/>
                                    <TextField
                                        className={className + '__inner__field__input__duration__textField'}
                                        type='number'
                                        size='small'
                                        InputProps={{ inputProps: { min: 0, max: 60 } }}
                                        variant='outlined'
                                        required/>
                                    <TextField
                                        className={className + '__inner__field__input__duration__textField'}
                                        type='number'
                                        size='small'
                                        InputProps={{ inputProps: { min: 0, max: 60 } }}
                                        variant='outlined'
                                        required/>
                                </div>
                                <div className={className + '__inner__field__input__duration flex-row-center-1vw-gap'}>
                                    <div className={className + '__inner__field__input__duration__headers '}>
                                        Days
                                    </div>
                                    <div className={className + '__inner__field__input__duration__headers '}>
                                        Hours
                                    </div>
                                    <div className={className + '__inner__field__input__duration__headers '}>
                                        Minutes
                                    </div>
                                    <div className={className + '__inner__field__input__duration__headers '}>
                                        Seconds
                                    </div>
                                </div>
                            </div>
                    }
                    {
                        props.field?.type == 'phoneNumber' &&
                            <div className={className + '__inner__field__input flex-column-center-1vh-gap'}>
                                <PhoneInput
                                    country='in'
                                    placeholder='Enter phone number'/>
                            </div>
                    }
                    {
                        props.field?.type == 'rating' &&
                            <div className={className + '__inner__field__input flex-row-center-1vw-gap'}>
                                {
                                    ratingType === 'select' &&
                                        <FormControl>
                                            <RadioGroup row>
                                                {
                                                    options?.map((option) =>
                                                        <FormControlLabel value={option.value} labelPlacement='top' control={<Radio />} label={
                                                            <div className={className + '__inner__body__field__multiChoice__value__select__option flex-row-center-1vw-gap'}>
                                                                {
                                                                    option.iconType === 'text' ?
                                                                        option.title
                                                                    :
                                                                        <Emoji
                                                                            unified={option.iconValue.unified}
                                                                            size={25}/>
                                                                }
                                                            </div>
                                                        } />
                                                    )
                                                }
                                            </RadioGroup>
                                        </FormControl>
                                }
                                {
                                    ratingType === 'range' &&
                                        <div className={className + '__inner__field__input__range flex-row-center-1vw-gap'}>
                                            <div className={className + '__inner__field__input__range__initial standard-text-semi-large semi-bold-text'}>
                                                {ratingInitialLabel}
                                            </div>
                                            <FormControl className={className + '__inner__field__input__range__list flex-column-center-2vh-gap'}>
                                                <RadioGroup
                                                    row>
                                                    {
                                                        options.map((option, index) =>
                                                            <FormControlLabel labelPlacement='top' control={<Radio checked={false} />} label={option.value} />
                                                        )
                                                    }
                                                </RadioGroup>
                                            </FormControl>
                                            <div className={className + '__inner__field__input__range__final standard-text-semi-large semi-bold-text'}>
                                                {ratingFinalLabel}
                                            </div>
                                        </div>
                                }
                                {
                                    ratingType === 'scale' &&
                                        <div className={className + '__inner__field__input__range flex-row-center-1vw-gap'}>
                                            {
                                                ratingScaleIcon === 'star' &&
                                                    <Rating defaultValue={1} max={Number(ratingRange)} size='large'/>
                                            }
                                            {
                                                ratingScaleIcon === 'heart' &&
                                                    <HeartStyledRating
                                                        defaultValue={1}
                                                        getLabelText={(value: number) => `${value} Heart${value !== 1 ? 's' : ''}`}
                                                        max={Number(ratingRange)}
                                                        icon={<FavoriteIcon fontSize='inherit' />}
                                                        size='large'
                                                        emptyIcon={<FavoriteBorderIcon fontSize='inherit' />}/>
                                            }
                                            {
                                                ratingScaleIcon === 'circle' &&
                                                    <CircleStyledRating
                                                        defaultValue={1}
                                                        getLabelText={(value: number) => `${value} Heart${value !== 1 ? 's' : ''}`}
                                                        max={Number(ratingRange)}
                                                        icon={<LensIcon fontSize='inherit' />}
                                                        size='large'
                                                        emptyIcon={<RadioButtonUncheckedIcon fontSize='inherit' />}/>
                                            }
                                        </div>
                                }
                            </div>
                    }
                </div>
                <div className={className + '__inner__bottom width-wfa flex-row-center-space-between'}>
                    <div/>
                    <div className={className + '__inner__bottom__t1 semi-bold-text'}>
                        {props.index + 1}/{props.fieldPageFieldIdList.length}
                    </div>
                </div>
                {
//                    props.field?.type !== 'information' &&
//                    <Grid container xs={12} className={className + '__inner__field '}>
//                        <Collapsible
//                            trigger={
//                                <Grid item xs={12} className={className + '__inner__field__trigger flex-row-center-1vw-gap'}>
//                                    <PathArrow className={className + '__inner__field__pathArrow dashboard-icon standard-link'}
//                                        id={'arrow__KRF' + props.index}
//                                        onClick={toggleCollapsibleOpen}/>
//                                    <Grid item xs={12} className={className + '__inner__field__trigger standard-text-semi-large'}>
//                                        Rules
//                                    </Grid>
//                                </Grid>
//                            }
//                            open={collapsibleOpen}
//                            transitionTime={200}
//                            triggerDisabled={true}
//                            classParentString={className + '__inner__field__collapsable width-wfa'}>
//                                {
//                                    collapsibleOpen &&
//                                        <div className={className + '__inner__field__collapsable__inner'}>
//                                            {
//                                                props?.field?.type == 'multiChoice' &&
//                                                <div className={className + '__inner__multiChoice width-wfa flex-column-flex-start-1vh-gap'}>
//                                                    <div className={className + '__inner__multiChoice__default width-wfa flex-column-flex-start-1vh-gap'}>
//                                                        <div className={className + '__inner__multiChoice__default__t1 semi-bold-text'}>
//                                                            Default
//                                                        </div>
//                                                        <div className={className + '__inner__multiChoice__default__t1 width-wfa flex-row-center-1vw-gap'}>
//                                                            <div className={className + '__inner__body__field__multiChoice__value__select__option indicator-box'}
//                                                                style={{background: defaultOption?.color}}/>
//                                                                {defaultOption?.title}
//                                                        </div>
//                                                    </div>
//                                                    <div className={className + '__inner__multiChoice__options width-wfa flex-column-flex-start-1vh-gap'}>
//                                                        <Grid item xs={12} className={className + '__inner__multiSelect__t1 semi-bold-text'}>
//                                                            Rules
//                                                        </Grid>
//                                                        <Grid container xs={12} className={className + '__inner__multiSelect__flex semi-bold-text'}>
//                                                            <Grid item xs={8} className={className + '__inner__multiSelect__flex__t1'}>
//                                                                Title
//                                                            </Grid>
//                                                            <Grid item xs={4} className={className + '__inner__multiSelect__flex__t2'}>
//                                                                Value
//                                                            </Grid>
//                                                        </Grid>
//                                                        {
//                                                            options?.map((option) =>
//                                                                <Grid container xs={12} className={className + '__inner__multiSelect__flex__option'}>
//                                                                    <Grid item xs={8} className={className + '__inner__multiSelect__flex__option__title flex-row-center-1vw-gap'}>
//                                                                        <div className={className + '__inner__multiSelect__flex__option__title__indicator indicator-box '}
//                                                                            style={{background: option.color}}/>
//                                                                        {option.title}
//                                                                    </Grid>
//                                                                    <Grid item xs={4} className={className + '__inner__multiSelect__flex__option__value'}>
//                                                                        {option.value}
//                                                                    </Grid>
//                                                                </Grid>
//                                                            )
//                                                        }
//                                                    </div>
//                                                </div>
//                                            }
//                                            {
//                                                props?.field?.type == 'multiSelect' &&
//                                                <div className={className + '__inner__multiSelect width-wfa flex-column-flex-start-1vh-gap'}>
//                                                    <Grid item xs={12} className={className + '__inner__multiSelect__t1 semi-bold-text'}>
//                                                        Rules
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__multiSelect__flex semi-bold-text'}>
//                                                        <Grid item xs={8} className={className + '__inner__multiSelect__flex__t1'}>
//                                                            Title
//                                                        </Grid>
//                                                        <Grid item xs={4} className={className + '__inner__multiSelect__flex__t2'}>
//                                                            Value
//                                                        </Grid>
//                                                    </Grid>
//                                                    {
//                                                        options?.map((option) =>
//                                                            <Grid container xs={12} className={className + '__inner__multiSelect__flex__option'}>
//                                                                <Grid item xs={8} className={className + '__inner__multiSelect__flex__option__title flex-row-center-1vw-gap'}>
//                                                                    <div className={className + '__inner__multiSelect__flex__option__title__indicator indicator-box '}
//                                                                        style={{background: option.color}}/>
//                                                                    {option.title}
//                                                                </Grid>
//                                                                <Grid item xs={4} className={className + '__inner__multiSelect__flex__option__value'}>
//                                                                    {option.value}
//                                                                </Grid>
//                                                            </Grid>
//                                                        )
//                                                    }
//                                                </div>
//                                            }
//                                            {
//                                                props.field?.type == 'text' &&
//                                                <div className={className + '__inner__text width-wfa flex-column-flex-start-1vh-gap'}>
//                                                    <Grid item xs={12} className={className + '__inner__text__t1 semi-bold-text'}>
//                                                        Rules
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__text__property semi-bold-text'}>
//                                                        <Grid item xs={4} className={className + '__inner__text__property__t1'}>
//                                                            Max Width
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__text__property'}>
//                                                        <Grid item xs={4} className={className + '__inner__text__property__value'}>
//                                                            {rules?.find((rule) => rule.type === 'width')?.value}
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__text__property semi-bold-text'}>
//                                                        <Grid item xs={4} className={className + '__inner__text__property__t1'}>
//                                                            Default
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__text__property'}>
//                                                        <Grid item xs={4} className={className + '__inner__text__property__value'}>
//                                                            {rules?.find((rule) => rule?.type === 'default')?.value}
//                                                        </Grid>
//                                                    </Grid>
//                                                </div>
//                                            }
//                                            {
//                                                props.field?.type == 'file' &&
//                                                <div className={className + '__inner__file width-wfa flex-column-flex-start-1vh-gap'}>
//                                                    <Grid item xs={12} className={className + '__inner__file__t1 semi-bold-text'}>
//                                                        Rules
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__file__fileTypes semi-bold-text'}>
//                                                        <Grid item xs={6} className={className + '__inner__file__fileTypes__t1'}>
//                                                            Accepted File Types
//                                                        </Grid>
//                                                        <Grid item xs={6} className={className + '__inner__file__size'}>
//                                                            Max File Size
//                                                        </Grid>
//                                                    </Grid>
//                                                    {
//                                                        rules?.find((rule) => rule.type === 'acceptTypes')?.value?.map((fileType) =>
//                                                            <Grid container xs={12} className={className + '__inner__file__fileTypes__option'}>
//                                                                <Grid item xs={6} className={className + '__inner__file__fileTypes__option__t1'}>
//                                                                    {fileType}
//                                                                </Grid>
//                                                                <Grid item xs={6} className={className + '__inner__file__fileTypes__option__t2'}>
//                                                                    2MB
//                                                                </Grid>
//                                                            </Grid>
//                                                        )
//                                                    }
//                                                </div>
//                                            }
//                                            {
//                                                props.field?.type == 'duration' &&
//                                                <div className={className + '__inner__duration width-wfa flex-column-flex-start-1vh-gap'}>
//                                                    <Grid item xs={12} className={className + '__inner__duration__t1 semi-bold-text'}>
//                                                        Rules
//                                                    </Grid>
//                                                    {
//                                                        rules?.find((rule) => rule.type === 'minDuration').set &&
//                                                            <Grid container xs={12} className={className + '__inner__duration__width'}>
//                                                                <Grid item xs={4} className={className + '__inner__duration__width__t1'}>
//                                                                    Min Width
//                                                                </Grid>
//                                                                <Grid item xs={4} className={className + '__inner__duration__width__value'}>
//                                                                    {rules?.find((rule) => rule.type === 'minDuration').days} days &nbsp;
//                                                                    {rules?.find((rule) => rule.type === 'minDuration').hours} hours &nbsp;
//                                                                    {rules?.find((rule) => rule.type === 'minDuration').minutes} minutes &nbsp;
//                                                                    {rules?.find((rule) => rule.type === 'minDuration').seconds} seconds
//                                                                </Grid>
//                                                            </Grid>
//                                                    }
//                                                    {
//                                                        rules?.find((rule) => rule.type === 'maxDuration').set &&
//                                                            <Grid container xs={12} className={className + '__inner__duration__width'}>
//                                                                <Grid item xs={4} className={className + '__inner__duration__width__t1'}>
//                                                                    Max Width
//                                                                </Grid>
//                                                                <Grid item xs={4} className={className + '__inner__duration__width__value'}>
//                                                                    {rules?.find((rule) => rule.type === 'maxDuration').days} days &nbsp;
//                                                                    {rules?.find((rule) => rule.type === 'maxDuration').hours} hours &nbsp;
//                                                                    {rules?.find((rule) => rule.type === 'maxDuration').minutes} minutes &nbsp;
//                                                                    {rules?.find((rule) => rule.type === 'maxDuration').seconds} seconds
//                                                                </Grid>
//                                                            </Grid>
//                                                    }
//                                                </div>
//                                            }
//                                            {
//                                                props?.field?.type == 'rating' &&
//                                                <div className={className + '__inner__rating width-wfa flex-column-flex-start-1vh-gap'}>
//                                                    {JSON.stringify(props.field.rules)}
//                                                    <Grid item xs={12} className={className + '__inner__rating__t1 semi-bold-text'}>
//                                                        Rules
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__rating__flex semi-bold-text'}>
//                                                        <Grid item xs={8} className={className + '__inner__rating__flex__t1'}>
//                                                            Title
//                                                        </Grid>
//                                                        <Grid item xs={4} className={className + '__inner__rating__flex__t2'}>
//                                                            Value
//                                                        </Grid>
//                                                    </Grid>
//                                                    {
//                                                        options?.map((option) =>
//                                                            <Grid container xs={12} className={className + '__inner__rating__flex__option'}>
//                                                                <Grid item xs={8} className={className + '__inner__rating__flex__option__title flex-row-center-1vw-gap'}>
//                                                                    {
//                                                                        option.iconType === 'text' ?
//                                                                            option.title
//                                                                        :
//                                                                            <Emoji
//                                                                                unified={option.iconValue.unified}
//                                                                                size={25}/>
//                                                                    }
//                                                                </Grid>
//                                                                <Grid item xs={4} className={className + '__inner__rating__flex__option__value'}>
//                                                                    {option.value}
//                                                                </Grid>
//                                                            </Grid>
//                                                        )
//                                                    }
//                                                </div>
//                                            }
//                                            {
//                                                props.field?.type == 'number' &&
//                                                <div className={className + '__inner__number width-wfa flex-column-flex-start-1vh-gap'}>
//                                                    <Grid item xs={12} className={className + '__inner__number__t1 semi-bold-text'}>
//                                                        Rules
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__number__width semi-bold-text'}>
//                                                        <Grid item xs={4} className={className + '__inner__number__width__t1'}>
//                                                            Maximum Digits
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__number__width'}>
//                                                        <Grid item xs={4} className={className + '__inner__number__width__value'}>
//                                                            {rules?.find((rule) => rule.type === 'digitWidth')?.value ?? 10}
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__number__width semi-bold-text'}>
//                                                        <Grid item xs={4} className={className + '__inner__number__width__t1'}>
//                                                            Entry Range
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__number__width'}>
//                                                        <Grid item xs={4} className={className + '__inner__number__width__value'}>
//                                                            {rules?.find((rule) => rule.type === 'rangeMin')?.value ?? 'N/A'} : {rules?.find((rule) => rule.type === 'rangeMax')?.value ?? 'N/A'}
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__number__width semi-bold-text'}>
//                                                        <Grid item xs={4} className={className + '__inner__number__width__t1'}>
//                                                            Match Digit Width
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__number__width'}>
//                                                        <Grid item xs={4} className={className + '__inner__number__width__value'}>
//                                                            {rules?.find((rule) => rule.type === 'matchDigitWidth')?.value ? JSON.stringify(rules?.find((rule) => rule.type === 'matchDigitWidth')?.value) : 'false'}
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__text__property semi-bold-text'}>
//                                                        <Grid item xs={4} className={className + '__inner__text__property__t1'}>
//                                                            Default
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__text__property'}>
//                                                        <Grid item xs={4} className={className + '__inner__text__property__value'}>
//                                                            {rules?.find((rule) => rule?.type === 'default')?.value}
//                                                        </Grid>
//                                                    </Grid>
//                                                </div>
//                                            }
//                                            {
//                                                props.field?.type == 'phoneNumber' &&
//                                                <div className={className + '__inner__phoneNumber width-wfa flex-column-flex-start-1vh-gap'}>
//                                                    <Grid item xs={12} className={className + '__inner__phoneNumber__t1 semi-bold-text'}>
//                                                        Rules
//                                                    </Grid>
//                                                </div>
//                                            }
//                                            {
//                                                props.field?.type == 'date' &&
//                                                <div className={className + '__inner__date width-wfa flex-column-flex-start-1vh-gap'}>
//                                                    <Grid container xs={12} className={className + '__inner__date__width semi-bold-text'}>
//                                                        <Grid item xs={4} className={className + '__inner__date__width__t1'}>
//                                                            Allow Prior Date Entry?
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__date__width'}>
//                                                        <Grid item xs={4} className={className + '__inner__date__width__value'}>
//                                                            {
//                                                                rules?.find((rule) => rule.type === 'allowPrior')?.value ?
//                                                                    JSON.stringify(rules?.find((rule) => rule.type === 'allowPrior')?.value)
//                                                                :
//                                                                    'false'
//                                                            }
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__date__width semi-bold-text'}>
//                                                        <Grid item xs={4} className={className + '__inner__date__width__t1'}>
//                                                            Allow Time Selection?
//                                                        </Grid>
//                                                    </Grid>
//                                                    <Grid container xs={12} className={className + '__inner__date__width'}>
//                                                        <Grid item xs={4} className={className + '__inner__date__width__value'}>
//                                                            {
//                                                                rules?.find((rule) => rule.type === 'timeSelect')?.value ?
//                                                                    JSON.stringify(rules?.find((rule) => rule.type === 'timeSelect')?.value)
//                                                                :
//                                                                    'false'
//                                                            }
//                                                        </Grid>
//                                                    </Grid>
//                                                </div>
//                                            }
//                                        </div>
//                                }
//                        </Collapsible>
//                    </Grid>
                }
            </div>
            {
                showDeleteFieldForm &&
                    <DeleteFieldForm
                        open={showDeleteFieldForm}
                        closePopup={toggleDeleteFieldForm}
                        fieldPage={props.fieldPage}
                        kaiziqResolveConnection={props.kaiziqResolveConnection}
                        field={props.field}
                        renderData={props.renderData}
                        handleDeleteFieldClick={props.handleDeleteFieldClick}
                        index={props.index}/>
            }
            {
                showFieldView &&
                    <FieldView
                        open={showFieldView}
                        closePopup={toggleShowFieldView}
                        field={props.field}
                        kaiziqResolveConnection={props.kaiziqResolveConnection}
                        setCursor={setCursor}
                        cursor={cursor}
                        updateField={updateField}
                        index={props.index}/>
            }
        </div>
    )
}
